
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { navigateToPricing } from '@/utils/pricingNavigation';

const Navbar = () => {
  const [showSignIn, setShowSignIn] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    console.log("Navbar component mounted, path:", location.pathname);
    
    // Safely check auth status without using the useAuth hook directly
    try {
      // Check local storage for a simple indication if user might be logged in
      // This is just a UI hint, actual auth checks happen in protected routes
      const hasAuthToken = localStorage.getItem('supabase.auth.token') || 
                          Object.keys(localStorage).some(key => key.startsWith('sb-'));
      setIsAuthenticated(!!hasAuthToken);
    } catch (e) {
      // Fail silently - default to logged out UI
      setIsAuthenticated(false);
    }
    
    return () => console.log("Navbar component unmounted");
  }, [location.pathname]);
  
  const isPublicPage = [
    "/", 
    "/signup", 
    "/signin", 
    "/pricing", 
    "/privacy-policy", 
    "/terms-and-conditions", 
    "/disclaimer", 
    "/cancellation-guide",
    "/cookie-policy", 
    "/accessibility-statement",
    "/pitch-guide", 
    "/lead-capture"
  ].includes(location.pathname);

  if (!isPublicPage) {
    console.log("Not rendering Navbar - not a public page");
    return null;
  }

  console.log("🟢 Rendering Navbar, current path:", location.pathname);
  
  const handleGetStarted = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log("🟢 Navbar: GET STARTED CLICKED - direct navigation");
    window.location.href = '/signup?t=' + Date.now();
  };
  
  const handleDashboard = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log("🟢 Navbar: DASHBOARD CLICKED - direct navigation");
    window.location.href = '/dashboard?t=' + Date.now();
  };
  
  const handleSignIn = () => {
    // Direct navigation to sign in page
    console.log("Sign in navigation");
    window.location.href = '/signin?t=' + Date.now();
  };
  
  const handleSignOut = async () => {
    try {
      console.log("🟢 Navbar: SIGN OUT CLICKED");
      
      // Clean up auth state
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Force page reload for a clean state
      window.location.href = '/?t=' + Date.now();
    } catch (error) {
      console.error("Error signing out:", error);
      // Fallback redirect if the signOut function fails
      window.location.href = '/?t=' + Date.now();
    }
  };

  // Logo rendering based on device type
  const renderLogo = () => {
    if (isMobile) {
      return (
        <div className="flex items-start">
          <img 
            src="/lovable-uploads/5ff0c1a6-9b40-485d-bb61-6427fd6e304e.png" 
            alt="Startup Fundraising Logo" 
            className="h-14 w-auto object-contain" // Increased from h-12 to h-14 for mobile
          />
        </div>
      );
    }

    return (
      <div className="flex items-center">
        <img 
          src="/lovable-uploads/5ff0c1a6-9b40-485d-bb61-6427fd6e304e.png" 
          alt="Startup Fundraising Logo" 
          className="h-10 w-auto object-contain" 
        />
        <span className="ml-3 text-xs font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">BETA</span>
      </div>
    );
  };

  return (
    <>
      <nav className={`fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border/40 ${isMobile ? 'px-4' : 'px-6'}`}>
        <div className="mx-auto flex items-center justify-between h-16 w-full">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              {renderLogo()}
            </Link>
          </div>
          
          <div className="flex items-center space-x-4 ml-auto">
            {isAuthenticated ? (
              <>
                <Button 
                  variant="outline" 
                  className="text-foreground border-border"
                  onClick={handleSignOut}
                  size={isMobile ? "sm" : "default"}
                >
                  Sign Out
                </Button>
                <Button 
                  className="bg-ai-blue hover:bg-ai-blue/90 text-white" 
                  onClick={handleDashboard}
                  data-dashboard="true"
                  size={isMobile ? "sm" : "default"}
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  className="text-foreground border-border"
                  onClick={handleSignIn}
                  size={isMobile ? "sm" : "default"}
                >
                  Sign In
                </Button>
                <Button 
                  className="bg-ai-blue hover:bg-ai-blue/90 text-white" 
                  onClick={handleGetStarted}
                  size={isMobile ? "sm" : "default"}
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
